/* Emberwall theme: header, menus, dialogs, announcement bar, pop-up. Vanilla JS, no dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Dialog helpers (native <dialog> with an exit transition) ---------- */
  const Dialog = {
    open(dialog, opener) {
      if (!dialog || dialog.open) return;
      dialog._opener = opener || document.activeElement;
      dialog.showModal();
      requestAnimationFrame(() => dialog.classList.add('is-open'));
    },
    close(dialog) {
      if (!dialog || !dialog.open) return;
      dialog.classList.remove('is-open');
      const done = () => {
        dialog.close();
        if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus({ preventScroll: true });
      };
      if (reduceMotion.matches) return done();
      let finished = false;
      const finish = () => { if (!finished) { finished = true; done(); } };
      dialog.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    },
    wire(dialog) {
      if (!dialog || dialog._wired) return;
      dialog._wired = true;
      dialog.addEventListener('cancel', (e) => { e.preventDefault(); Dialog.close(dialog); });
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) Dialog.close(dialog);
        if (e.target.closest('[data-close]')) Dialog.close(dialog);
      });
    }
  };
  window.EwDialog = Dialog;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (!opener) return;
    const dialog = document.getElementById(opener.dataset.openDialog);
    if (!dialog) return;
    e.preventDefault();
    Dialog.wire(dialog);
    Dialog.open(dialog, opener);
    const focusTarget = dialog.querySelector('[data-autofocus]');
    if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
  });

  /* ---------- Header: transparent, frosted on scroll, hides when scrolling down ---------- */
  class EwHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.site-header-section') || this;
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;
      this.ticking = false;
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => { this.update(); this.ticking = false; });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      const hoverMq = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
      this.hovering = false;
      this.addEventListener('mouseenter', () => { if (hoverMq.matches) { this.hovering = true; this.update(); } });
      this.addEventListener('mouseleave', () => { this.hovering = false; this.update(); });
      this.update();
      this.setupMega();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    update() {
      const y = Math.max(window.scrollY, 0);
      this.classList.toggle('is-scrolled', y > 8 || this.hovering === true);
      if (this.hideOnScroll && !this.classList.contains('is-open')) {
        const goingDown = y > this.lastY;
        const past = y > this.section.offsetTop + this.offsetHeight * 2;
        this.section.classList.toggle('is-hidden', goingDown && past);
      }
      this.lastY = y;
    }
    setupMega() {
      this.overlay = this.querySelector('.mega-overlay');
      const triggers = this.querySelectorAll('[data-mega-trigger]');
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      let closeTimer;
      triggers.forEach((btn) => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        const open = () => { clearTimeout(closeTimer); this.openMega(btn, panel); };
        const scheduleClose = () => { clearTimeout(closeTimer); closeTimer = setTimeout(() => this.closeMega(), 180); };
        btn.addEventListener('click', () => (btn.getAttribute('aria-expanded') === 'true' ? this.closeMega() : open()));
        btn.parentElement.addEventListener('mouseenter', () => { if (finePointer.matches) open(); });
        btn.parentElement.addEventListener('mouseleave', () => { if (finePointer.matches) scheduleClose(); });
        panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
        panel.addEventListener('mouseleave', () => { if (finePointer.matches) scheduleClose(); });
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.current) { const b = this.current; this.closeMega(); b.focus(); } });
      document.addEventListener('click', (e) => { if (this.current && !this.contains(e.target)) this.closeMega(); });
      if (this.overlay) this.overlay.addEventListener('click', () => this.closeMega());
    }
    openMega(btn, panel) {
      if (this.current && this.current !== btn) this.closeMega();
      this.current = btn;
      btn.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      this.classList.add('is-open');
      this.section.classList.remove('is-hidden');
      if (this.overlay) this.overlay.classList.add('is-visible');
    }
    closeMega() {
      if (!this.current) return;
      const panel = document.getElementById(this.current.getAttribute('aria-controls'));
      this.current.setAttribute('aria-expanded', 'false');
      if (panel) panel.classList.remove('is-open');
      this.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      this.current = null;
    }
  }
  if (!customElements.get('ew-header')) customElements.define('ew-header', EwHeader);

  /* ---------- Announcement bar ----------
     Desktop: the grouped messages stay side by side, and each "alone" message takes over the full width in turn.
     Mobile: every message takes its turn, one at a time. */
  class EwAnnouncement extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.all = Array.from(this.querySelectorAll('.announcement__item')).sort((a, b) => a.dataset.order - b.dataset.order);
      this.alone = this.all.filter((i) => i.classList.contains('announcement__item--alone'));
      this.groupMs = (parseInt(this.dataset.groupSeconds, 10) || 7) * 1000;
      this.aloneMs = (parseInt(this.dataset.aloneSeconds, 10) || 4) * 1000;
      this.mobileMs = (parseInt(this.dataset.seconds, 10) || 4) * 1000;
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => (this.paused = true)));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => (this.paused = false)));
      this.onChange = () => this.start();
      this.desktop.addEventListener('change', this.onChange);
      this.start();
    }
    disconnectedCallback() { clearTimeout(this.timer); if (this.desktop) this.desktop.removeEventListener('change', this.onChange); }
    visible(list) {
      const hidden = this.desktop.matches ? 'hide-desktop' : 'hide-mobile';
      return list.filter((i) => !i.classList.contains(hidden));
    }
    start() {
      clearTimeout(this.timer);
      this.classList.remove('is-alone');
      this.all.forEach((i) => i.classList.remove('is-active'));
      this.idx = 0;
      if (this.desktop.matches) {
        this.seq = this.visible(this.alone);
        this.phase = 'group';
        if (this.seq.length && !reduceMotion.matches) this.schedule(this.groupMs);
      } else {
        this.seq = this.visible(this.all);
        if (this.seq[0]) this.seq[0].classList.add('is-active');
        if (this.seq.length > 1 && !reduceMotion.matches) this.schedule(this.mobileMs);
      }
    }
    schedule(ms) { this.timer = setTimeout(() => this.advance(), ms); }
    showAlone() {
      this.seq.forEach((i) => i.classList.remove('is-active'));
      this.seq[this.idx].classList.add('is-active');
      this.classList.add('is-alone');
    }
    advance() {
      if (this.paused || document.hidden) { this.schedule(1000); return; }
      if (this.desktop.matches) {
        if (this.phase === 'group') {
          this.phase = 'alone'; this.idx = 0; this.showAlone(); this.schedule(this.aloneMs);
        } else {
          this.idx += 1;
          if (this.idx < this.seq.length) { this.showAlone(); this.schedule(this.aloneMs); }
          else { this.phase = 'group'; this.classList.remove('is-alone'); this.seq.forEach((i) => i.classList.remove('is-active')); this.schedule(this.groupMs); }
        }
      } else {
        this.seq[this.idx].classList.remove('is-active');
        this.idx = (this.idx + 1) % this.seq.length;
        this.seq[this.idx].classList.add('is-active');
        this.schedule(this.mobileMs);
      }
    }
  }
  if (!customElements.get('ew-announcement')) customElements.define('ew-announcement', EwAnnouncement);

  /* ---------- Carousel: arrows scroll by one view, and are hidden when there is nothing more to see ---------- */
  /* ---------- Height of the announcement bar, so a full-screen hero ends exactly at the bottom of the screen ---------- */
  const setAnnouncementHeight = () => {
    const bar = document.querySelector('.announcement-section');
    document.documentElement.style.setProperty('--announcement-h', `${bar ? bar.offsetHeight : 0}px`);
  };
  setAnnouncementHeight();
  window.addEventListener('resize', setAnnouncementHeight);

  class EwCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      const go = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      if (this.prev) this.prev.addEventListener('click', () => go(-1));
      if (this.next) this.next.addEventListener('click', () => go(1));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
        this.classList.toggle('is-static', max <= 0);
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    disconnectedCallback() { window.removeEventListener('resize', this.update); }
  }
  if (!customElements.get('ew-carousel')) customElements.define('ew-carousel', EwCarousel);

  /* ---------- Hero slider: fades between slides, plays on its own, pauses on hover, swipes on touch ---------- */
  class EwHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.index = 0;
      this.delay = (parseInt(this.dataset.seconds, 10) || 6) * 1000;
      this.setAccent();
      if (this.slides.length < 2) return;
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.show(i); this.restart(); }));
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => this.pause()));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => this.resume()));
      let x0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) { this.show(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
      });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i >= 0) { this.show(i); this.pause(); }
      });
      this.restart();
    }
    disconnectedCallback() { clearTimeout(this.timer); }
    setAccent() {
      const s = this.slides[this.index];
      this.dataset.accent = s ? s.dataset.accent : 'none';
    }
    show(i) {
      const n = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, k) => { s.classList.toggle('is-active', k === n); s.setAttribute('aria-hidden', String(k !== n)); });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === n));
      this.index = n;
      this.setAccent();
    }
    restart() {
      clearTimeout(this.timer);
      this.classList.remove('is-paused');
      this.dots.forEach((d) => { const f = d.firstElementChild; if (f) { f.style.animation = 'none'; void f.offsetWidth; f.style.animation = ''; } });
      if (reduceMotion.matches) { this.classList.add('is-paused'); return; }
      this.timer = setTimeout(() => { this.show(this.index + 1); this.restart(); }, this.delay);
    }
    pause() { clearTimeout(this.timer); this.classList.add('is-paused'); }
    resume() { if (this.classList.contains('is-paused') && !reduceMotion.matches) this.restart(); }
  }
  if (!customElements.get('ew-hero')) customElements.define('ew-hero', EwHero);

  /* ---------- Accordions: smooth open and close for every <details class="disclosure"> ---------- */
  const EwAccordion = {
    bind(details) {
      if (details.dataset.accordion) return;
      details.dataset.accordion = 'true';
      const summary = details.querySelector('summary');
      const content = details.querySelector('.disclosure__content');
      if (!summary || !content) return;
      summary.addEventListener('click', (e) => {
        if (reduceMotion.matches) return;
        e.preventDefault();
        if (details.dataset.animating) return;
        details.dataset.animating = 'true';
        const done = () => { delete details.dataset.animating; content.style.height = ''; content.style.overflow = ''; };
        content.style.overflow = 'hidden';
        if (details.open) {
          const from = content.offsetHeight;
          const anim = content.animate([{ height: `${from}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 280, easing: 'cubic-bezier(.4,0,.2,1)' });
          details.classList.add('is-closing');
          anim.onfinish = () => { details.open = false; details.classList.remove('is-closing'); done(); };
        } else {
          details.open = true;
          const to = content.offsetHeight;
          const anim = content.animate([{ height: '0px', opacity: 0 }, { height: `${to}px`, opacity: 1 }], { duration: 340, easing: 'cubic-bezier(.2,.7,.2,1)' });
          anim.onfinish = done;
        }
      });
    },
    init(root = document) { root.querySelectorAll('details.disclosure').forEach((d) => EwAccordion.bind(d)); }
  };
  EwAccordion.init();
  document.addEventListener('shopify:section:load', (e) => EwAccordion.init(e.target));

  /* ---------- Gentle reveal of section content as it scrolls into view ---------- */
  if ('IntersectionObserver' in window && !reduceMotion.matches) {
    const revealIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); revealIO.unobserve(entry.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    const revealTargets = '.block-head, .features__head, .feature, .type-tile, .card-tile, .quiz-teaser, .iwt__content, .review-card, .quote, .cta, .size-band';
    document.querySelectorAll(revealTargets).forEach((el, i) => {
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.classList.add('reveal');
      el.style.setProperty('--reveal-delay', `${(i % 4) * 60}ms`);
      revealIO.observe(el);
    });
  }

  /* ---------- Search preview: products and suggestions under the field as you type ---------- */
  const EwPredictive = {
    bind(form) {
      const input = form.querySelector('input[name="q"]');
      if (!input || form.dataset.predictive) return;
      form.dataset.predictive = 'true';
      const panel = document.createElement('div');
      panel.className = 'ps-panel';
      panel.id = `ps-${Math.random().toString(36).slice(2, 8)}`;
      panel.hidden = true;
      form.classList.add('has-ps');
      form.appendChild(panel);
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-controls', panel.id);
      input.setAttribute('aria-expanded', 'false');
      let timer;
      let controller;
      const close = () => { panel.hidden = true; input.setAttribute('aria-expanded', 'false'); };
      const open = () => { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); };
      const run = () => {
        const q = input.value.trim();
        if (q.length < 2) { close(); return; }
        if (controller) controller.abort();
        controller = new AbortController();
        const url = `${(window.Ew && window.Ew.routes && window.Ew.routes.predictive) || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product,query,collection&resources[limit]=4&resources[options][unavailable_products]=last&section_id=predictive-search`;
        fetch(url, { signal: controller.signal })
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const results = doc.querySelector('[data-ps-results]');
            if (!results) { close(); return; }
            panel.replaceChildren(results);
            open();
          })
          .catch(() => {});
      };
      input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 180); });
      input.addEventListener('focus', () => { if (panel.childElementCount && input.value.trim().length >= 2) open(); });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { close(); return; }
        if (e.key === 'ArrowDown' && !panel.hidden) {
          const first = panel.querySelector('[data-ps-item]');
          if (first) { e.preventDefault(); first.focus(); }
        }
      });
      panel.addEventListener('keydown', (e) => {
        const items = Array.from(panel.querySelectorAll('[data-ps-item]'));
        const i = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' && i < items.length - 1) { e.preventDefault(); items[i + 1].focus(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); (i > 0 ? items[i - 1] : input).focus(); }
        if (e.key === 'Escape') { close(); input.focus(); }
      });
      document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
    },
    init(root = document) { root.querySelectorAll('form[role="search"]').forEach((f) => EwPredictive.bind(f)); }
  };
  EwPredictive.init();

  /* Links marked data-open-newsletter open the sign-up pop-up */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open-newsletter]');
    if (!trigger) return;
    const popup = document.querySelector('ew-popup');
    if (popup && typeof popup.show === 'function') { e.preventDefault(); popup.show(true); }
  });

  /* ---------- Delivery dates: "12 to 19 October" from day offsets ---------- */
  const formatDeliveryDates = (root = document) => {
    root.querySelectorAll('[data-delivery-dates]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return;
      const add = (d) => { const t = new Date(); t.setDate(t.getDate() + d); return t; };
      const a = add(min), b = add(max);
      const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric' });
      const full = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' });
      el.textContent = a.getMonth() === b.getMonth() ? `${day.format(a)} to ${full.format(b)}` : `${full.format(a)} to ${full.format(b)}`;
    });
  };
  window.EwFormatDeliveryDates = formatDeliveryDates;
  formatDeliveryDates();

  /* ---------- Newsletter pop-up ---------- */
  /* Shopify cookie banner: keep the pop-up and the offer tab out of its way */
  const EwCookieBanner = {
    open() {
      const b = document.getElementById('shopify-pc__banner');
      if (!b) return false;
      const cs = getComputedStyle(b);
      return cs.display !== 'none' && cs.visibility !== 'hidden' && b.getBoundingClientRect().height > 0;
    },
    sync() { document.documentElement.classList.toggle('has-cookie-banner', EwCookieBanner.open()); }
  };
  let ewCookieTick = false;
  new MutationObserver(() => {
    if (ewCookieTick) return;
    ewCookieTick = true;
    setTimeout(() => { ewCookieTick = false; EwCookieBanner.sync(); }, 60);
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
  document.addEventListener('visitorConsentCollected', () => setTimeout(EwCookieBanner.sync, 400));
  EwCookieBanner.sync();

  class EwPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.tab = this.querySelector('.offer-tab');
      if (!this.dialog) return;
      Dialog.wire(this.dialog);
      this.key = 'ew-popup';
      this.days = parseInt(this.dataset.frequency, 10) || 7;
      this.dialog.addEventListener('close', () => this.onClosed());
      this.querySelectorAll('[data-popup-dismiss]').forEach((b) => b.addEventListener('click', () => Dialog.close(this.dialog)));
      const form = this.querySelector('form');
      if (form) form.addEventListener('submit', (e) => this.submit(e, form));
      const copy = this.querySelector('[data-copy-code]');
      if (copy) copy.addEventListener('click', () => this.copyCode(copy));
      if (this.tab) {
        this.tab.querySelector('.offer-tab__open').addEventListener('click', () => this.show(true));
        this.tab.querySelector('.offer-tab__close').addEventListener('click', () => { this.store('tab', Date.now()); this.tab.hidden = true; });
      }
      if (this.querySelector('[data-popup-posted]')) { this.showSuccess(); this.show(true); return; }
      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:section:select', (ev) => { if (ev.target.contains(this)) this.show(true); });
        document.addEventListener('shopify:section:deselect', (ev) => { if (ev.target.contains(this)) Dialog.close(this.dialog); });
        return;
      }
      if (this.read('subscribed')) return;
      if (this.recently('dismissed')) { this.showTab(); return; }
      this.armTriggers();
    }
    read(k) { try { return JSON.parse(localStorage.getItem(`${this.key}:${k}`)); } catch (e) { return null; } }
    store(k, v) { try { localStorage.setItem(`${this.key}:${k}`, JSON.stringify(v)); } catch (e) { /* storage blocked */ } }
    recently(k) { const t = this.read(k); return t && Date.now() - t < this.days * 864e5; }
    armTriggers() {
      const fire = () => { this.disarm(); this.show(); };
      const delay = parseInt(this.dataset.delay, 10);
      if (delay > 0) this.timer = setTimeout(fire, delay * 1000);
      const pct = parseInt(this.dataset.scroll, 10);
      if (pct > 0) {
        this.onScroll = () => {
          const h = document.documentElement.scrollHeight - innerHeight;
          if (h > 0 && (scrollY / h) * 100 >= pct) fire();
        };
        addEventListener('scroll', this.onScroll, { passive: true });
      }
      if (this.dataset.exitIntent === 'true' && matchMedia('(pointer: fine)').matches) {
        this.onLeave = (e) => { if (!e.relatedTarget && e.clientY <= 0) fire(); };
        document.addEventListener('mouseout', this.onLeave);
      }
    }
    disarm() {
      clearTimeout(this.timer);
      if (this.onScroll) removeEventListener('scroll', this.onScroll);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
    }
    show(force) {
      if (!force && EwCookieBanner.open()) { setTimeout(() => this.show(), 2500); return; }
      if (!force && document.querySelector('dialog[open]')) { setTimeout(() => this.show(), 4000); return; }
      if (this.tab) this.tab.hidden = true;
      Dialog.open(this.dialog);
    }
    onClosed() {
      if (this.read('subscribed')) return;
      this.store('dismissed', Date.now());
      this.showTab();
    }
    showTab() { if (this.tab && !this.recently('tab')) this.tab.hidden = false; }
    showSuccess() {
      this.dialog.classList.add('is-success');
      this.querySelector('[data-popup-step="form"]').hidden = true;
      this.querySelector('[data-popup-step="success"]').hidden = false;
      this.store('subscribed', true);
      if (this.tab) this.tab.hidden = true;
    }
    async submit(e, form) {
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const msg = form.querySelector('[data-form-message]');
      if (btn.classList.contains('is-loading')) return;
      if (msg) msg.hidden = true;
      btn.classList.add('is-loading');
      const send = async () => {
        const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' }, credentials: 'same-origin' });
        if (/challenge/.test(res.url)) return 'challenge';
        const html = await res.text();
        const posted = res.redirected || res.url.includes('customer_posted=true') || html.includes('customer_posted=true') || html.includes('data-popup-posted') || html.includes('form--success');
        const failed = html.includes('data-popup-error');
        if (posted || (res.ok && !failed)) return 'ok';
        console.warn('[popup] unexpected response', res.status, res.url);
        return 'fail';
      };
      try {
        let result = await send();
        if (result === 'fail') { await new Promise((r) => setTimeout(r, 700)); result = await send(); }
        if (result === 'challenge') { form.submit(); return; }
        if (result === 'ok') { this.showSuccess(); return; }
        if (msg) msg.hidden = false;
      } catch (err) {
        form.submit();
      } finally {
        btn.classList.remove('is-loading');
      }
    }
    async copyCode(btn) {
      const code = btn.dataset.copyCode;
      try { await navigator.clipboard.writeText(code); btn.textContent = btn.dataset.copiedLabel || 'Copied'; } catch (e) { /* clipboard blocked */ }
    }
  }
  if (!customElements.get('ew-popup')) customElements.define('ew-popup', EwPopup);
  /* ---------- Product recommendations (loaded when near the viewport) ---------- */
  class EwRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid') || !this.dataset.url) return;
      const load = () => fetch(this.dataset.url).then((r) => r.text()).then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('ew-recommendations');
        if (fresh && fresh.querySelector('.product-grid')) { this.replaceChildren(...fresh.childNodes); return; }
        const section = this.closest('.shopify-section');
        if (section) section.hidden = true;
      }).catch(() => {});
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  if (!customElements.get('ew-recommendations')) customElements.define('ew-recommendations', EwRecommendations);
})();
